import React, { useRef } from 'react';
import {
    SymbolTileData,
    SymbolTileDataTimeSeriesItem,
} from '../symbolTileData';

import './SymbolTileChart.scss';
import { getSymbolTilesSeries } from 'widgets/symbolTile/components/chart/series/symbolTileSeries';
import { XAxisContextProvider } from 'utils/components/TimeSeriesChart/XAxisContext';
import { extentLinear } from '@d3fc/d3fc-extent';
import { scaleLinear } from 'd3-scale';
import { styleConstants } from './variables.module.constants';
import { styleConstants as tileStyleConstants } from 'widgets/tiles/components/styles/variables.module.constants';
import AreaGradient from 'utils/components/TimeSeriesChart/AreaGradient';
import { useWidgetContext } from 'widgets/WidgetContext';
import { TileTimeSeriesChart } from 'widgets/tiles/components/tile/TileTimeSeriesChart';
import { SymbolTileChartHeaderValues } from 'widgets/symbolTile/components/chart/SymbolTileChartHeaderValues';

export interface SymbolTileChartProps {
    data: SymbolTileData;
}

export function SymbolTileChart(props: SymbolTileChartProps) {
    const { widgetKey } = useWidgetContext();
    const { data } = props;
    const { timeSeries, startTime, endTime } = data;

    const priceGradientId = `priceGradientId-${widgetKey}`;
    const height =
        tileStyleConstants.tileHeight -
        tileStyleConstants.tileWidgetToolbarHeight -
        tileStyleConstants.tileWidgetToolbarMarginTop -
        styleConstants.priceChangeRowHeight;
    const yExtent = extentLinear()
        .accessors([(a: SymbolTileDataTimeSeriesItem) => a.price])
        .pad([0.25, 0.1]);

    const yScaleRef = useRef(scaleLinear());
    const yDomain = yExtent(timeSeries);
    yScaleRef.current.domain(yDomain);

    const chartSeries = getSymbolTilesSeries(priceGradientId, yDomain[0]);

    // Pad x-axis in case blobs are at start or end
    const duration = endTime! - startTime!;
    const tickPerPx = duration / tileStyleConstants.tileWidth;
    const pxPadd = 5 * tickPerPx;
    const paddedStartTime = startTime! - pxPadd;
    const paddedEndTime = endTime! + pxPadd;

    return (
        <div className="SymbolTileChart flippable">
            {startTime && endTime && (
                <XAxisContextProvider
                    timeSeriesChartWidth={tileStyleConstants.tileWidth}
                    startTime={paddedStartTime}
                    endTime={paddedEndTime}
                    breaks={[]}
                    rangeLabels={[]}
                    timeLineLabels={[]}
                >
                    <SymbolTileChartHeaderValues data={data} />
                    <TileTimeSeriesChart
                        id="SymbolTileTimeSeriesChart"
                        height={height}
                        data={timeSeries}
                        series={chartSeries}
                        displayXAxis={true}
                        yScale={yScaleRef.current}
                        yTicks={0}
                    />
                    <AreaGradient
                        identifier={priceGradientId}
                        className="price-gradient"
                        startOffset="0%"
                        stopOffset="100%"
                    />
                </XAxisContextProvider>
            )}
        </div>
    );
}
